import { useState, useEffect, useRef } from 'react';
import { SEVERITY, CATEGORY } from './useEventBus.js';
import { pushToast } from '../components/AlertToast.jsx';

const API = import.meta.env.VITE_API_BASE || '/api';

export function useReentry(push) {
  const [reentries, setReentries] = useState([]);
  const alerted = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    async function fetch_() {
      try {
        const res = await fetch(`${API}/reentry`);
        if (!res.ok) return;
        const data = await res.json();
        const list = data.reentries || [];
        if (!cancelled) {
          setReentries(list);
          // Event thresholds, tight to avoid feed noise:
          // Toast only: < 6h (genuinely imminent, warrants interruption)
          // Feed event: < 24h (worth knowing today)
          // Silence everything beyond 24h
          for (const r of list) {
            const daysLeft = (new Date(r.decay_epoch) - Date.now()) / 86400000;
            if (daysLeft > 1) continue; // only within 24h
            const hoursLeft = daysLeft * 24;
            const key = `${r.norad_id}-${hoursLeft < 6 ? 'crit' : 'warn'}`;
            if (!alerted.current.has(key)) {
              alerted.current.add(key);
              const sev = hoursLeft < 6 ? SEVERITY.CRITICAL : SEVERITY.WARNING;
              push?.({ severity: sev, category: CATEGORY.REENTRY,
                title: `⚠ Reentry: ${r.name}`,
                body:  `Predicted in ${hoursLeft.toFixed(0)}h ± ${r.uncertainty_days != null ? (r.uncertainty_days * 24).toFixed(0) : '?'}h`,
                norad_1: r.norad_id });
              // Toast only for < 6h, don't interrupt for 20h predictions
              if (hoursLeft < 6) pushToast({ severity: SEVERITY.CRITICAL,
                title: `⚠ REENTRY in ${hoursLeft.toFixed(0)}h`,
                category:CATEGORY.REENTRY, timing:`In about ${hoursLeft.toFixed(0)}h · uncertainty ± ${r.uncertainty_days!=null?(r.uncertainty_days*24).toFixed(0):'?'}h`,
                body:  r.name, norad_1: r.norad_id });
            }
          }
        }
      } catch { /* keep last */ }
    }
    fetch_();
    const id = setInterval(fetch_, 60 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return { reentries };
}

export function useGroundStations() {
  const [stations, setStations] = useState([]);
  useEffect(() => {
    let cancelled = false;
    async function fetch_() {
      try {
        const res = await fetch(`${API}/groundstations`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStations(data.stations || []);
      } catch { /* keep last */ }
    }
    fetch_();
    return () => { cancelled = true; };
  }, []);
  return { stations };
}

export function useISSDetail() {
  const [crew,   setCrew]   = useState([]);
  const [issNow, setIssNow] = useState(null);
  useEffect(() => {
    let cancelled = false;
    async function fetchCrew() {
      try {
        const res = await fetch(`${API}/iss/crew`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCrew((data.people || []).filter(p => p.craft === 'ISS'));
      } catch { /* keep last */ }
    }
    async function fetchNow() {
      try {
        const res = await fetch(`${API}/iss/now`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setIssNow(data);
      } catch { /* keep last */ }
    }
    fetchCrew();
    fetchNow();
    const id = setInterval(fetchNow, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  return { crew, issNow };
}
